import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  SimpleGrid,
} from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { ptBR } from 'date-fns/locale';
import { IoChevronBackCircleOutline, IoChevronForwardCircleOutline } from "react-icons/io5";
import { getUser, isRepresentante, shouldHideFeeDetails } from "../../libs/auth";
import { useLimites } from "../../hooks/pdv/useLimites";
import { getAgenciasFromUser } from "../../utils/agency";

registerLocale('ptBR', ptBR)

type FilterFormFields = {
  ordem_trabalho?: string;
  status?: string;
  data_inicial?: Date;
  data_final?: Date;
  pedido?: string;
  usuario?: string;
  agencia?: string;
  passageiro?: string;
};

function toDateString(date: Date | null | undefined) {
  if (!date) {
    return undefined;
  }

  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function formatInputDate(value: string | Date | null | undefined) {
  if (typeof value === 'string' || value === undefined || value === null) {
    return value || undefined;
  }

  if (Number.isNaN(value.getTime())) {
    return undefined;
  }

  return toDateString(value);
}

export function Filter() {
  const { setValue } = useFormContext();
  const representanteOnly = isRepresentante();
  const hideFeeDetails = shouldHideFeeDetails();
  const { data: limitesData } = useLimites();

  const representanteMinDate = useMemo(() => {
    const valor = limitesData?.relatorioRepresentanteDataLimite;

    if (!valor || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
      return new Date(2026, 1, 1);
    }

    const [ano, mes, dia] = valor.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }, [limitesData?.relatorioRepresentanteDataLimite]);

  const currentMonth = useMemo(() => {
    const now = new Date();

    return {
      dataInicial: new Date(now.getFullYear(), now.getMonth(), 1),
      dataFinal: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
  }, []);

  const form = useForm<FilterFormFields>({
    defaultValues: {
      data_inicial: currentMonth.dataInicial,
      data_final: currentMonth.dataFinal,
      status: representanteOnly ? "3" : undefined,
    },
  });

  const { register, getValues, watch } = form;
  const user = getUser();
  const status = watch('status') || '';

  const agencias = useMemo(() => getAgenciasFromUser(user), [user]);
  const shouldShowAgenciaSelect = !representanteOnly && agencias.length > 1;

  const onSubmit = () => {
    const values = getValues();
    const dataInicial = values.data_inicial && values.data_inicial < representanteMinDate
      ? representanteMinDate
      : values.data_inicial;
    const dataFinal = values.data_final && values.data_final < representanteMinDate
      ? representanteMinDate
      : values.data_final;

    setValue('filter', {
      ordem_trabalho: representanteOnly || hideFeeDetails ? undefined : values.ordem_trabalho,
      status: representanteOnly ? "3" : values.status,
      data_inicial: formatInputDate(dataInicial),
      data_final: formatInputDate(dataFinal),
      pedido: representanteOnly ? undefined : values.pedido,
      usuario: representanteOnly ? undefined : values.usuario,
      passageiro: representanteOnly ? undefined : values.passageiro,
      agencia: representanteOnly ? undefined : values.agencia,
    });
  };

  useEffect(() => {
    if (!representanteOnly && status !== "") {
      onSubmit();
    }
  }, [status, representanteOnly]);

  useEffect(() => {
    setValue('filter', {
      status: representanteOnly ? "3" : undefined,
      data_inicial: formatInputDate(currentMonth.dataInicial),
      data_final: formatInputDate(currentMonth.dataFinal),
    });
  }, [currentMonth.dataFinal, currentMonth.dataInicial, representanteOnly, setValue]);

  return (
    <FormProvider {...form}>
    <Box fontSize={'sm'} px={{xs: 2, md: 10}} py={8} border={"1px solid #ccc"} display={"flex"} flexDir={'column'}>
      <SimpleGrid columns={{xs: 1, md: 3}} w={'full'} gap={0}>
        {!representanteOnly && !hideFeeDetails && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Ordem de Trabalho</FormLabel>
            <Input type="text" size={'xs'} {...register("ordem_trabalho")} />
          </FormControl>
        )}
        {!representanteOnly && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Status</FormLabel>
            <Select size={'xs'} {...register("status")}>
              <option value="">Escolha</option>
              <option value="1">Aberto</option>
              <option value="2">Reservado</option>
              <option value="3">Finalizado</option>
              <option value="4">Cancelado</option>
              <option value="7">Devolvido</option>
              <option value="8">Devolvido Parcialmente</option>
            </Select>
          </FormControl>
        )}
        <FormControl px={3} display={'flex'} alignItems={'center'} >
          <FormLabel width={{xs: '50%', md: '30%'}}>Data Inicial</FormLabel>
            <Controller
              name="data_inicial"
              control={form.control}
              render={({ field: { onChange, onBlur, ref, value, name } }) => ( 
              <DatePicker
                selectsDisabledDaysInRange
                
                onChange={onChange} 
                onBlur={onBlur} 
                minDate={representanteOnly ? representanteMinDate : undefined}
                maxDate={new Date()}
                ref={ref} 
                name={name} 
                locale={"ptBR"}
                selected={value ?? null}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                customInput={
                  <Input
                    width={'full'}
                    type="text"
                    size={'xs'}
                    value={value instanceof Date ? toDateString(value) : ''}
                    name={name}
                    ref={ref}
                  />
                }
                renderCustomHeader={({
                  monthDate,
                  customHeaderCount,
                  decreaseMonth,
                  increaseMonth,
                }) => (
                <Flex justifyContent={'space-between'} alignItems={'center'} backgroundColor={'#e0cf0b'}>
                  <IconButton
                    aria-label="Mês Anterior"
                    variant={'unstyled'}
                    display={'flex'}
                    icon={<IoChevronBackCircleOutline size={24} />}
                    style={customHeaderCount === 1 ? { visibility: "hidden" } : undefined}
                    onClick={decreaseMonth}
                    _hover={{ color: "gray" }}
                  />
                  <span className="react-datepicker__current-month">
                    {monthDate.toLocaleString("pt-BR", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <IconButton
                    aria-label="Mês Posterior"
                    variant={'unstyled'}
                    icon={<IoChevronForwardCircleOutline size={24} />}
                    style={customHeaderCount === 0 ? { visibility: "hidden" } : undefined}
                    onClick={increaseMonth}
                    _hover={{ color: "gray" }}
                  />
                </Flex>
              )}
              monthsShown={2}
              
              />
            )}
        />
        </FormControl>
        {!representanteOnly && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Nº Pedido</FormLabel>
            <Input width={'100%'} type="number" size={'xs'} {...register("pedido")} />
          </FormControl>
        )}
        {!representanteOnly && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Usuário</FormLabel>
            <Input width={'100%'} type="text" size={'xs'} {...register("usuario")} />
          </FormControl>
        )}
        <FormControl px={3} display={'flex'} alignItems={'center'}>
          <FormLabel width={{xs: '50%', md: '30%'}}>Data Final</FormLabel>
          <Controller
              name="data_final"
              control={form.control}
              render={({ field: { onChange, onBlur, ref, value, name } }) => ( 
              <DatePicker
                selectsDisabledDaysInRange
                
                onChange={onChange} 
                onBlur={onBlur} 
                minDate={representanteOnly ? representanteMinDate : undefined}
                maxDate={new Date()}
                ref={ref} 
                name={name} 
                locale={"ptBR"}
                selected={value ?? null}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                customInput={
                  <Input
                    width={'full'}
                    type="text"
                    size={'xs'}
                    value={value instanceof Date ? toDateString(value) : ''}
                    name={name}
                    ref={ref}
                  />
                }
                renderCustomHeader={({
                  monthDate,
                  customHeaderCount,
                  decreaseMonth,
                  increaseMonth,
                }) => (
                <Flex justifyContent={'space-between'} alignItems={'center'} backgroundColor={'#e0cf0b'}>
                  <IconButton
                    aria-label="Mês Anterior"
                    variant={'unstyled'}
                    display={'flex'}
                    icon={<IoChevronBackCircleOutline size={24} />}
                    style={customHeaderCount === 1 ? { visibility: "hidden" } : undefined}
                    onClick={decreaseMonth}
                    _hover={{ color: "gray" }}
                  />
                  <span className="react-datepicker__current-month">
                    {monthDate.toLocaleString("pt-BR", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <IconButton
                    aria-label="Mês Posterior"
                    variant={'unstyled'}
                    icon={<IoChevronForwardCircleOutline size={24} />}
                    style={customHeaderCount === 0 ? { visibility: "hidden" } : undefined}
                    onClick={increaseMonth}
                    _hover={{ color: "gray" }}
                  />
                </Flex>
              )}
              monthsShown={2}
              
              />
            )}
        />
        </FormControl>
        {shouldShowAgenciaSelect && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Agência</FormLabel>
            <Select size={'xs'} {...register("agencia")}>
              <option value="">Escolha</option>
              {agencias.map((agencia) => {
                const value =
                  typeof agencia === "string"
                    ? agencia
                    : typeof agencia === "number"
                      ? String(agencia)
                      : agencia.id ?? agencia.value ?? agencia.nome ?? agencia.label ?? "";
                const label =
                  typeof agencia === "string"
                    ? agencia
                    : typeof agencia === "number"
                      ? String(agencia)
                      : agencia.nome ?? agencia.label ?? agencia.value ?? agencia.id ?? "";

                if (!value) return null;

                return (
                  <option key={value} value={value}>
                    {label || value}
                  </option>
                );
              })}
            </Select>
          </FormControl>
        )}
        {!representanteOnly && (
          <FormControl px={3} display={'flex'} alignItems={'center'}>
            <FormLabel width={{xs: '50%', md: '30%'}}>Nome do Passageiro</FormLabel>
            <Input type="text" size={'xs'} {...register("passageiro")} />
          </FormControl>
        )}
      </SimpleGrid>
      <Flex justifyContent={'end'} mt={4}>
        <Button onClick={onSubmit} colorScheme="blue" borderRadius={'none'} size={'sm'}>Pesquisar</Button>
      </Flex>
    </Box>
    </FormProvider>
  );
}
